import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { DateRange } from 'react-day-picker';
import { useFormContext } from 'react-hook-form';

import { ExtendedDateRange, ScheduleTimeFrame } from 'common/contracts';
import dayjs from 'dayjs';
import { getDurationTextFromMinutes } from 'utils/formattingUtil';

import Button from 'components/Button/Button';
import DatePicker from 'components/DatePicker/DatePicker';
import {
  DatePickerMode,
  DatePickerValue
} from 'components/DatePicker/DatePicker.types';
import Input from 'components/Input/Input';
import { Label } from 'components/ui/label';

import { AvailabilityInput } from './AvailabilityInput';
import { HideAvailabilityCheckbox } from './HideAvailabilityCheckbox';
import ScheduleAppearancesTable from './ScheduleAppearancesTable';

const SCHEDULE_FIELD_NAME = 'schedule';

interface IScheduleDateRangeProps {
  editRangeDatesValues: ExtendedDateRange | null;
  setEditRangeDatesValues: React.Dispatch<
    React.SetStateAction<ExtendedDateRange | null>
  >;
  appearancesTableData: ScheduleTimeFrame[];
  resetScheduleValues: () => void;
  showAvailability?: boolean;
}

const ScheduleDateRange: FC<IScheduleDateRangeProps> = ({
  editRangeDatesValues,
  setEditRangeDatesValues,
  appearancesTableData,
  resetScheduleValues,
  showAvailability = true
}): React.ReactElement => {
  const form = useFormContext();
  const values = form.watch();
  const playerAvailabilityError = form.formState.errors.playerAvailability;
  const playerAvailabilityTouched =
    form.formState.touchedFields.playerAvailability;

  const [localDateRange, setLocalDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined
  });
  const [localNotes, setLocalNotes] = useState('');
  const [formattedDuration, setFormattedDuration] = useState<string | null>(
    null
  );

  const clearLocalDateRange = useCallback((): void => {
    setLocalDateRange({ from: undefined, to: undefined });
  }, []);

  useEffect(() => {
    if (localDateRange.from && localDateRange.to) {
      const diffMinutes = dayjs(localDateRange.to).diff(
        dayjs(localDateRange.from),
        'minute'
      );
      const duration = getDurationTextFromMinutes(diffMinutes);
      setFormattedDuration(duration);
    } else {
      setFormattedDuration(null);
    }
  }, [localDateRange]);

  useEffect(() => {
    if (editRangeDatesValues) {
      const [startDate, endDate, notes] = editRangeDatesValues;
      setLocalDateRange({ from: startDate, to: endDate });
      setLocalNotes(notes || '');
    } else {
      clearLocalDateRange();
      setLocalNotes('');
    }
  }, [editRangeDatesValues, clearLocalDateRange]);

  const handleApply = useCallback((): void => {
    const fromUtcDate = localDateRange.from;
    const toUtcDate = localDateRange.to;

    if (!fromUtcDate || !toUtcDate) {
      return;
    }

    const newTimeFrame: ScheduleTimeFrame = {
      startTime: new Date(fromUtcDate.toISOString()),
      endTime: new Date(toUtcDate.toISOString()),
      notes: localNotes
    };

    if (editRangeDatesValues) {
      const [editStartTime, editEndTime] = editRangeDatesValues;
      const editStartTimestamp = editStartTime.getTime();
      const editEndTimestamp = editEndTime.getTime();

      const updatedTimeFrames = values?.schedule?.timeFrames.map(
        (timeFrame: ScheduleTimeFrame) => {
          const currentStartTimestamp = new Date(
            timeFrame.startTime
          ).getTime();
          const currentEndTimestamp = new Date(timeFrame.endTime).getTime();

          if (
            currentStartTimestamp === editStartTimestamp &&
            currentEndTimestamp === editEndTimestamp
          ) {
            return newTimeFrame;
          }
          return timeFrame;
        }
      );

      form.setValue(
        SCHEDULE_FIELD_NAME,
        { permanent: false, timeFrames: updatedTimeFrames },
        { shouldDirty: true }
      );
    } else {
      form.setValue(
        SCHEDULE_FIELD_NAME,
        {
          permanent: false,
          timeFrames: [...values?.schedule?.timeFrames, newTimeFrame]
        },
        { shouldDirty: true }
      );
    }

    clearLocalDateRange();
    setLocalNotes('');
    setEditRangeDatesValues(null);
    resetScheduleValues();
  }, [
    localDateRange,
    localNotes,
    editRangeDatesValues,
    values?.schedule?.timeFrames,
    form,
    clearLocalDateRange,
    setEditRangeDatesValues,
    resetScheduleValues
  ]);

  const handleCancel = useCallback((): void => {
    clearLocalDateRange();
    setLocalNotes('');
    setEditRangeDatesValues(null);
    resetScheduleValues();
  }, [clearLocalDateRange, setEditRangeDatesValues, resetScheduleValues]);

  const handleEditTimeframe = useCallback(
    (
      startTime: Date | string,
      endTime: Date | string,
      notes: string
    ): void => {
      const start =
        startTime instanceof Date ? startTime : new Date(startTime);
      const end = endTime instanceof Date ? endTime : new Date(endTime);
      setEditRangeDatesValues([start, end, notes]);
    },
    [setEditRangeDatesValues]
  );

  const handleDateRangeApply = useCallback(
    (value: DatePickerValue): void => {
      setLocalDateRange(value as DateRange);
    },
    []
  );

  const handleNotesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setLocalNotes(e.target.value);
    },
    []
  );

  const isApplyDisabled = useMemo((): boolean => {
    return (
      !localDateRange.from ||
      !localDateRange.to ||
      (!!playerAvailabilityTouched && !!playerAvailabilityError)
    );
  }, [
    localDateRange.from,
    localDateRange.to,
    playerAvailabilityTouched,
    playerAvailabilityError
  ]);

  const buttonLabel = useMemo((): string => {
    if (editRangeDatesValues) {
      return 'Apply';
    }
    return 'Add Appearance';
  }, [editRangeDatesValues]);

  const scheduleValue = useMemo(
    () => ({
      permanent: values?.schedule?.permanent,
      timeFrames: values?.schedule?.timeFrames || []
    }),
    [values?.schedule?.permanent, values?.schedule?.timeFrames]
  );

  const handleScheduleChange = useCallback(
    (newSchedule: { permanent?: boolean; timeFrames: unknown[] }): void => {
      form.setValue(SCHEDULE_FIELD_NAME, newSchedule, { shouldDirty: true });
    },
    [form]
  );

  return (
    <div className="space-y-9">
      <div className="w-full">
        <div className="flex gap-4">
          {showAvailability && (
            <div className="w-[20%]">
              <AvailabilityInput
                id="availability-scheduled"
                placeholder="1"
              />
            </div>
          )}
          <div className="flex-1">
            <DatePicker
              label="Pick A Date"
              labelSpacing="mb-2"
              shouldDisplayTime={true}
              shouldExcludePast={true}
              mode={DatePickerMode.RANGE}
              value={localDateRange}
              onApply={handleDateRangeApply}
            />
          </div>
        </div>
        {showAvailability && (
          <div className="mt-5">
            <HideAvailabilityCheckbox />
          </div>
        )}
        {formattedDuration && (
          <div className="space-y-2 mt-5">
            <Label className="text-sm text-zinc-900 font-medium">
              Duration
            </Label>
            <Input value={formattedDuration} disabled />
          </div>
        )}
        <div className="space-y-2 mt-5">
          <Input
            label="Note"
            name="notes"
            value={localNotes}
            placeholder="Write something..."
            onChange={handleNotesChange}
          />
        </div>
        <div className="space-y-3 mt-6">
          {editRangeDatesValues && (
            <Button
              disabled={!localDateRange.from || !localDateRange.to}
              onClick={handleCancel}
              className="w-full bg-base-accent text-base-accentForeground hover:bg-button-hover-create"
            >
              Cancel
            </Button>
          )}
          <Button
            disabled={isApplyDisabled}
            onClick={handleApply}
            className="w-full bg-base-accent text-base-accentForeground"
            isCreateButton
          >
            {buttonLabel}
          </Button>
        </div>
      </div>
      {appearancesTableData.length > 0 && (
        <div className="w-full">
          <ScheduleAppearancesTable
            schedule={scheduleValue}
            onScheduleChange={handleScheduleChange}
            appearancesTableData={appearancesTableData}
            onEdit={handleEditTimeframe}
            onDelete={resetScheduleValues}
          />
        </div>
      )}
    </div>
  );
};

export { ScheduleDateRange };
